import {
  AsOf,
  HtsCatalogRow,
  ListCatalogRow,
  MatchRow,
  ParsedQuery,
  RecentScreen,
  RunInput,
  SavedFilter,
  SavedFilterKind,
  Scope,
  ScopeKey,
} from './types';
import moment from 'moment';
import { screeningCountries } from 'utils/screeningCountries';
import { Direction, ScreeningProfile } from './profiles';

export const NOMENCLATURE_INDEX = 'bite_app_commodities_nomenclature';
export const EVAL_LIMIT = 1000;
export const PAGE_SIZE = 50;
export const AI_ERROR_MARKER = 'Error evaluating threat level';

export const LIST_KEYS: ScopeKey[] = ['lens', 'country', 'agency', 'list'];
export const HTS_KEYS: ScopeKey[] = ['hlist', 'section', 'chapter', 'code'];

export const emptyScope = (): Scope => ({
  lens: [],
  country: [],
  agency: [],
  list: [],
  hlist: [],
  section: [],
  chapter: [],
  code: [],
});

export const parseQuery = (raw: string): ParsedQuery => {
  const text = (raw || '').trim();
  if (!text) {
    return { kind: 'empty', text: text, fmt: '', digits: 0 };
  }
  const digits = text.replace(/[\s.]/g, '');
  if (/^\d+$/.test(digits)) {
    const ok = [6, 8, 10].indexOf(digits.length) !== -1;
    let fmt = digits.slice(0, 4);
    if (digits.length >= 6) {
      fmt += `.${digits.slice(4, 6)}`;
    }
    if (digits.length >= 8) {
      fmt += `.${digits.slice(6, 8)}`;
    }
    if (digits.length >= 10) {
      fmt += `.${digits.slice(8, 10)}`;
    }
    return {
      kind: ok ? 'hts' : 'badhts',
      text: text,
      fmt: fmt,
      digits: digits.length,
    };
  }
  return { kind: 'desc', text: text, fmt: '', digits: 0 };
};

export const queryTerm = (parsed: ParsedQuery): string =>
  parsed.kind === 'hts'
    ? parsed.fmt
    : parsed.kind === 'desc'
    ? parsed.text
    : '';

export const querySubject = (parsed: ParsedQuery, fallback: string): string =>
  parsed.kind === 'hts'
    ? parsed.fmt
    : parsed.kind === 'desc'
    ? `“${parsed.text}”`
    : fallback;

export const sameQuery = (a: ParsedQuery, b: ParsedQuery): boolean =>
  a.kind === b.kind &&
  (a.kind === 'hts'
    ? a.fmt === b.fmt
    : a.text.toLowerCase() === b.text.toLowerCase());

export const sameAsOf = (a: AsOf, b: AsOf): boolean =>
  a.mode === b.mode &&
  (a.mode === 'today' || (a.from === b.from && a.to === b.to));

export const countScope = (s: Scope, htsFilters: boolean): number =>
  LIST_KEYS.reduce((t, k) => t + s[k].length, 0) +
  (htsFilters ? HTS_KEYS.reduce((t, k) => t + s[k].length, 0) : 0);

const bucketsOf = (node: any, key: string): any[] =>
  (node && node[key] && Array.isArray(node[key].buckets)
    ? node[key].buckets
    : []) as any[];

// `rootAgg` names the lens level: `listAgg` in the commodity indices,
// `entityAggs` in the entity index.
export const flattenListAggs = (
  aggs: unknown,
  rootAgg = 'listAgg',
): ListCatalogRow[] => {
  const rows: ListCatalogRow[] = [];
  bucketsOf(aggs, rootAgg).forEach((lens: any) => {
    bucketsOf(lens, 'countryAgg').forEach((country: any) => {
      bucketsOf(country, 'agencyAgg').forEach((agency: any) => {
        bucketsOf(agency, 'listNameAgg').forEach((list: any) => {
          rows.push({
            lens: String(lens.key),
            country: String(country.key),
            agency: String(agency.key),
            list: String(list.key),
            n: Number(list.doc_count) || 0,
          });
        });
      });
    });
  });
  return rows;
};

export const flattenHtsAggs = (aggs: unknown): HtsCatalogRow[] => {
  const rows: HtsCatalogRow[] = [];
  bucketsOf(aggs, 'hsAgg').forEach((section: any) => {
    bucketsOf(section, 'chapterAgg').forEach((chapter: any) => {
      bucketsOf(chapter, 'codeAgg').forEach((code: any) => {
        rows.push({
          section: String(section.key),
          chapter: String(chapter.key),
          code: String(code.key),
          n: Number(code.doc_count) || 0,
        });
      });
    });
  });
  return rows;
};

export const htsCodeLists = (aggs: unknown): { key: string; n: number }[] =>
  bucketsOf(aggs, 'listName').map((b: any) => ({
    key: String(b.key),
    n: Number(b.doc_count) || 0,
  }));

export interface CascadeOption {
  key: string;
  n: number;
  on: boolean;
}
export interface CascadeColumn {
  dim: ScopeKey;
  title: string;
  options: CascadeOption[];
  selected: number;
}

export const cascade = <R extends Record<string, any>>(
  rows: R[],
  dims: { key: ScopeKey; title: string }[],
  sel: Scope,
): CascadeColumn[] =>
  dims.map((d, idx) => {
    const upstream = dims.slice(0, idx);
    const pool = rows.filter((r) =>
      upstream.every(
        (u) => sel[u.key].length === 0 || sel[u.key].indexOf(r[u.key]) !== -1,
      ),
    );
    const counts: Record<string, number> = {};
    pool.forEach((r) => {
      const k = r[d.key];
      counts[k] = (counts[k] || 0) + (Number(r.n) || 0);
    });
    sel[d.key].forEach((v) => {
      if (counts[v] === undefined) {
        counts[v] = 0;
      }
    });
    const keys = Object.keys(counts).sort(
      (a, b) => counts[b] - counts[a] || a.localeCompare(b),
    );
    return {
      dim: d.key,
      title: d.title,
      selected: sel[d.key].length,
      options: keys.map((k) => ({
        key: k,
        n: counts[k],
        on: sel[d.key].indexOf(k) !== -1,
      })),
    };
  });

// Countries without an ISO code ("Multiple Countries", disputed regions) send
// none, so the screen stays unscoped rather than hiding country records.
export const riskCountryCode = (partner: string): string =>
  screeningCountries.find((c) => c.name === partner)?.iso2 || '';

// The list half of a scope as the search services read it. Each column of the
// Filters modal narrows the one to its right, so only the deepest level picked
// below the lens (listing country, agency, list) is sent.
export const listScopeParams = (
  scope: Scope,
): {
  lens: string[];
  listCountry: string[];
  agency: string[];
  listName: string[];
} => ({
  lens: scope.lens,
  listCountry:
    scope.agency.length > 0 || scope.list.length > 0 ? [] : scope.country,
  agency: scope.list.length > 0 ? [] : scope.agency,
  listName: scope.list,
});

export type ScreenPayloadInput = Pick<
  RunInput,
  'jurisdiction' | 'partner' | 'scope' | 'asOf' | 'archived'
> & {
  index: string;
  direction: Direction;
  parsed: ParsedQuery;
  pageNum: number;
};

export const buildScreenPayload = ({
  index,
  direction,
  jurisdiction,
  partner,
  parsed,
  scope,
  asOf,
  archived,
  pageNum,
}: ScreenPayloadInput): Record<string, unknown> => {
  const hts = jurisdiction.htsFilters;
  const hlists = hts ? scope.hlist : [];
  const sections = hts ? scope.section : [];
  const chapters = hts ? scope.chapter : [];
  const codes = hts ? scope.code : [];
  return {
    primary: queryTerm(parsed),
    indices: [index],
    tradeType: jurisdiction.tradeType,
    type: direction,
    ...listScopeParams(scope),
    listName: [...scope.list, ...hlists],
    hsCodeSection: codes.length > 0 || chapters.length > 0 ? [] : sections,
    hsCodeChapter: codes.length > 0 ? [] : chapters,
    hsCode: codes,
    dateRange:
      asOf.mode === 'range' && asOf.from && asOf.to ? [asOf.from, asOf.to] : [],
    archived: archived,
    riskCountry: riskCountryCode(partner),
    agg: true,
    pageNum: pageNum,
  };
};

const text = (v: any): string => {
  if (v === undefined || v === null) {
    return '';
  }
  if (Array.isArray(v)) {
    return v
      .filter((x) => x !== undefined && x !== null && x !== '')
      .join(', ');
  }
  return String(v);
};

export const toMatchRows = (hits: any[]): MatchRow[] =>
  (hits || []).map((hit: any, i: number) => {
    const s = (hit && hit._source) || {};
    return {
      id: String(hit?._id || i),
      name: text(s['Product Name']) || '--',
      hs: text(s['HS Code']) || '--',
      list: text(s['List Name']) || '--',
      agency: text(s.Agency) || '--',
      country:
        text(s['Source Country']) ||
        text(s['BITE-Identified Risk Country']) ||
        '--',
      date:
        text(s['Date Updated on BITE Platform']) ||
        text(s['Date Updated']) ||
        '--',
      source: s,
    };
  });

export const riskTone = (risk: string): 'high' | 'medium' | 'low' | 'none' => {
  const r = (risk || '').toLowerCase();
  if (r.indexOf('high') !== -1 || r.indexOf('severe') !== -1) {
    return 'high';
  }
  if (r.indexOf('med') !== -1 || r.indexOf('moderate') !== -1) {
    return 'medium';
  }
  if (r.indexOf('low') !== -1 || r.indexOf('minimal') !== -1) {
    return 'low';
  }
  return 'none';
};

// Kept only when written in uppercase, so "contact us" isn't read as the US.
const UPPERCASE_WORDS = ['US', 'EU', 'CA', 'HTS', 'ECCN'];

const titleWord = (word: string): string => {
  const lower = word
    .split(/([A-Za-z]+)/)
    .map((part) =>
      UPPERCASE_WORDS.indexOf(part) !== -1 ? part : part.toLowerCase(),
    )
    .join('');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const titleCase = (text: string): string =>
  (text || '').replace(/[^\s-]+/g, titleWord);

export const isoToday = (): string => moment().format('YYYY-MM-DD');

export const shortDate = (ms: number): string => moment(ms).format('MMM D');

export const longDate = (ms: number): string =>
  moment(ms).format('MMM D, YYYY');

export const rangeLabel = (asOf: AsOf): string => {
  if (asOf.mode !== 'range' || !asOf.from || !asOf.to) {
    return 'Today';
  }
  const from = moment(asOf.from, 'YYYY-MM-DD');
  const to = moment(asOf.to, 'YYYY-MM-DD');
  const fromFormat = from.year() === to.year() ? 'MMM D' : 'MMM D, YYYY';
  return `${from.format(fromFormat)} – ${to.format('MMM D, YYYY')}`;
};

const recentsKey = (direction: Direction, teamId: string) =>
  `bite.${direction.toLowerCase()}Screening.recents.${teamId || 'none'}`;

export const readRecents = (
  profile: ScreeningProfile,
  teamId: string,
): RecentScreen[] => {
  try {
    const raw = window.localStorage.getItem(
      recentsKey(profile.direction, teamId),
    );
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr)
      ? (arr as RecentScreen[]).filter(
          (r) =>
            r &&
            typeof r.q === 'string' &&
            profile.jurisdictions.some((j) => j.id === r.jurisdiction),
        )
      : [];
  } catch (e) {
    return [];
  }
};

export const writeRecent = (
  profile: ScreeningProfile,
  teamId: string,
  r: RecentScreen,
): RecentScreen[] => {
  const same = (a: RecentScreen) =>
    a.q.toLowerCase() === r.q.toLowerCase() &&
    a.partner === r.partner &&
    a.jurisdiction === r.jurisdiction;
  const next = [
    r,
    ...readRecents(profile, teamId).filter((a) => !same(a)),
  ].slice(0, 6);
  try {
    window.localStorage.setItem(
      recentsKey(profile.direction, teamId),
      JSON.stringify(next),
    );
  } catch (e) {}
  return next;
};

type SavedField =
  | 'lenses'
  | 'countries'
  | 'agencies'
  | 'lists'
  | 'hsCodeLists'
  | 'sections'
  | 'chapters'
  | 'codes';

const SAVED_FIELD: Record<ScopeKey, SavedField> = {
  lens: 'lenses',
  country: 'countries',
  agency: 'agencies',
  list: 'lists',
  hlist: 'hsCodeLists',
  section: 'sections',
  chapter: 'chapters',
  code: 'codes',
};

const keysOf = (kind: SavedFilterKind): ScopeKey[] =>
  kind === 'commodityCode' ? HTS_KEYS : LIST_KEYS;

export const savedFields = (
  s: Scope,
  kind?: SavedFilterKind,
): Partial<Record<SavedField, string[]>> => {
  const out: Partial<Record<SavedField, string[]>> = {};
  (kind ? keysOf(kind) : [...LIST_KEYS, ...HTS_KEYS]).forEach((k) => {
    out[SAVED_FIELD[k]] = s[k];
  });
  return out;
};

export const scopeFromSaved = (f: SavedFilter, base: Scope): Scope => {
  const s = { ...base };
  keysOf(f.type).forEach((k) => {
    s[k] = [...(f[SAVED_FIELD[k]] || [])];
  });
  return s;
};

export const savedSize = (f: SavedFilter): number =>
  keysOf(f.type).reduce((t, k) => t + (f[SAVED_FIELD[k]] || []).length, 0);

const sameSet = (a: string[], b: string[]) =>
  a.slice().sort().join('|') === b.slice().sort().join('|');

export const savedMatches = (f: SavedFilter, s: Scope): boolean => {
  const want = scopeFromSaved(f, emptyScope());
  return keysOf(f.type).every((k) => sameSet(want[k], s[k]));
};
